/*
© 2018-present Harald Rudell <harald.rudell@gmail.com> (http://www.haraldrudell.com)
This source code is licensed under the ISC-style license found in the LICENSE file in the root directory of this source tree.
*/
import React, {memo, useState, useCallback, useMemo} from 'react'
import styled, {keyframes, css} from 'styled-components'
import logo from './logo.svg'
import cover from './cover.png'
import es2049 from './ECMAScript 2049.png'
import LogoLine from './LogoLine'

const Button = styled.button`
background: palevioletred;
color: white;
font-size: 15pt;
border-radius: 10px;
border-color: purple;
`
const pinkTones = keyframes`
  from {
    color: hsl(0, 80%, 50%); // red
  }
  33% {
    color: black;
  }
  66% {
    color: hsl(280, 80%, 50%);
  }
  to {
    color: hsl(360, 80%, 50%);
  }
`
const AStyleFn = count => css`
font-weight: bold;
text-decoration: none;
${count ===1 && css`color: purple`}
animation: ${pinkTones} 8s linear ${count};
`
console.log('AStyleFN', AStyleFn('infinite'))
const MarginFn = aStyle => styled.div`
max-width: 8.5in;
padding: 0 3em;
> h2 {
  margin-top: 2em;
}
> h2 a {
  ${aStyle}
}
> dl {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 1em;
  grid-row-gap: 1em;
}
> dl dt {
  margin: 0; /* TODO remove */
}
> dl a {
  ${aStyle}
}
> dl dd {
  margin-left: 0;
}
> p {
  margin-top: 4em;
}
`
const rotate360 = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`
const LogoElementFn = count => styled.img`
height: 3em;
animation: ${rotate360} ${count} 20s linear;
`
const ReactFame = styled.div`
font: bolder 25pt 'Blade Runner';
color: red;
text-shadow: 0px 1px 4px #23430C;
text-align: center;
`
const Img = styled.img`
height: 4em;
`
const LeadInFn = aStyle => styled.div`
display: grid;
grid-template-columns: repeat(3, 1fr);
align-items: start;
a {
  display: block
  margin-bottom: 0.5em;
  ${aStyle}
}
button {
  display: block;
  cursor: pointer;
}
`
console.log('LeadInFn', LeadInFn(AStyleFn('infinite')))
export default memo(() => {
  const [animate, setAnimate] = useState(true)
  const animateAction = useCallback(() => setAnimate(!animate), [animate])
  const anmCount = animate ? 'infinite' : 1
  const Margin = useMemo(() => MarginFn(AStyleFn(anmCount)), [anmCount])
  const LeadIn = useMemo(() => LeadInFn(AStyleFn(anmCount)), [anmCount])
  const LogoElement = useMemo(() => LogoElementFn(anmCount), [anmCount])
  return <Margin>
    <h1>Harald Rudell’s Online Portfolio</h1>
    <LeadIn>
      <div>
        <a href="https://twitter.com/harud">Twitter</a>
        <a href="https://github.com/haraldrudell/">github open-sourced software</a>
        <a href="https://www.npmjs.com/~haraldrudell">npm authored packages</a>
      </div>
      <span><Button onClick={animateAction}>{`${animate ? 'Stop' : 'Start'} Animation`}</Button></span>
      <span>
        Depending on browser,<br />
        css3 animations on this page may load the cpu
      </span>
    </LeadIn>
    <h2>Online React Projects</h2>
    <dl>
      <dt><a href="https://hire.surge.sh/">Hire Harald</a></dt>
      <dd>A site serving my resume</dd>
      <dt>
        <a href="https://haraldrudell.github.io/redux/">Redux</a><br />
        November 2018
      </dt>
      <dd>
        <Row>
          <div>
            A React/Redux site with some complex forms<br />
            Material-UI styled-components immutable axios koa<br />
            <a href="https://github.com/haraldrudell/redux">source code</a>
          </div>
          <a href="https://haraldrudell.github.io/redux/">
            <LogoElement src={logo} alt="React logo" />
          </a>
        </Row>
      </dd>
      <dt>
        <a href="https://haraldrudell.github.io/demo-context-store/">Allstore</a><br />
        December 2018
      </dt>
      <dd>
        A single-truth store in <strong>94% less</strong> code-lines<br />
        featuring both <strong>react@next hooks</strong> and traditional connect <strong>high-order component</strong><br />
        on npm as <a href="https://www.npmjs.com/package/allstore">allstore</a><br />
        on github as <a href="https://github.com/haraldrudell/demo-context-store">demo-context-store</a><br />
        deep link to <a href="https://github.com/haraldrudell/demo-context-store/tree/master/src/allstore">allstore source code</a></dd>
      <dt>
        <a href="https://haraldrudell.github.io/react-fame/">React Fame</a><br />
        December 2018
      </dt>
      <dd><Row>
        <div>
          A React app published to npm as <a href="https://www.npmjs.com/package/react-fame">react-fame</a><br />
          designed while demonstrating <a href="https://github.com/haraldrudell/lib-create-react-app">lib-create-react-app</a><br />
          as shown in this <a href="https://youtu.be/KVaOVjiH2SQ">lib-create-react-app video 10 min</a><br />
          <a href="https://github.com/haraldrudell/react-fame">source code</a>
        </div>
        <a href="https://haraldrudell.github.io/react-fame/">
          <ReactFame>
            react<br />
            fame
          </ReactFame>
        </a>
      </Row></dd>
      <dt>
        <a href="https://haraldrudell.github.io/animation/">Animation</a><br />
        November 2018
      </dt>
      <dd>
        A React application demonstrating css3: custom fonts, gradients and animations<br />
        <a href="https://github.com/haraldrudell/animation">source code</a>
      </dd>
      <dt>
        <a href="https://haraldrudell.github.io/ssr/">Record Sleeve</a><br />
        December 2018
      </dt>
      <dd><Row>
        <div>
          Cover-page for a server-side rendering project for Create React App<br />
          published to npm as <a href="https://www.npmjs.com/package/ssr-create-react-app">ssr-create-react-app</a><br />
          tweeted to <strong>Dan Abramov</strong> after he said “<a href="https://twitter.com/dan_abramov/status/953296173116276736">SSR is hard</a>”<br />
          video: <a href="https://youtu.be/sn9InqfNtSQ">Server-side rendering for create react app, 11 min</a><br />
          <a href="https://github.com/haraldrudell/ssr">source code</a>
        </div>
        <a href="https://haraldrudell.github.io/ssr/">
          <Img src={cover} alt="faksimile" />
        </a>
      </Row></dd>
    </dl>

    <h2>Expertise in:&emsp;ReactJS&emsp;Golang&emsp;Node.js&emsp;Java</h2>
    <LogoLine animate={animate} />

    <h2>Video</h2>
    <dl>
      <dt><a href="https://youtu.be/sn9InqfNtSQ">Server-side rendering for create react app, 11 min</a></dt><dd />
      <dt><a href="https://youtu.be/KVaOVjiH2SQ">Publish a Create React App to npm, 10 min</a></dt><dd />
      <dt><a href="https://youtu.be/WaKGjhEaP4U">Theming React 16.6, 3 min</a></dt><dd />
      <dt><a href="https://youtu.be/cVPG2eWHd3Q">React 16.5.2 Material UI, 3 min</a></dt><dd />
    </dl>

    <h2>Npm Packages for Node.js and React</h2>
    <dl>
      <dt><a href="https://www.npmjs.com/~haraldrudell">Package List</a></dt>
      <dd>npm package list: 34+ since 2011</dd>
      <dt>
        <a href="https://www.npmjs.com/package/es2049package">es2049package</a><br />
        December 2017
      </dt>
      <dd><Row>
        <div>
          No-configuration transpilation of server-side libraries and executables<br />
          <a href="https://github.com/haraldrudell/ECMAScript2049/tree/master/workspace/packages/es2049package">source code</a>
        </div>
        <a href="https://www.npmjs.com/package/es2049package">
          <Img src={es2049} alt="ECMAScript2049 logo" />
        </a>
      </Row></dd>
      <dt>
        <a href="https://www.npmjs.com/package/es2049scripts">es2049scripts</a><br />
        December 2017
      </dt>
      <dd><Row>
        <div>
          On-the-fly transpilation of ECMAScript for instant ES.Next execution<br />
          <a href="https://github.com/haraldrudell/ECMAScript2049/tree/master/workspace/packages/es2049scripts">source code</a>
        </div>
        <a href="https://www.npmjs.com/package/es2049scripts">
          <Img src={es2049} alt="ECMAScript2049 logo" />
        </a>
      </Row></dd>
      <dt>
        <a href="https://www.npmjs.com/package/jest-esnext">jest-esnext</a><br />
        December 2018
      </dt>
      <dd>
        A self-contained jest package for reliable ES.Next testing
      </dd>
      <dt>
        <a href="https://www.npmjs.com/package/lib-create-react-app">lib-create-react-app</a><br />
        November 2018
      </dt>
      <dd><Row>
        <div>
          A Create React App add-on that publishes components to npm<br />
          <a href="https://youtu.be/KVaOVjiH2SQ">lib-create-react-app video (10 min)</a><br />
          <a href="https://github.com/haraldrudell/lib-create-react-app">source code</a>
        </div>
        <a href="https://www.npmjs.com/package/lib-create-react-app">
          <LoudSpeaker />
        </a>
      </Row></dd>
      <dt>
        <a href="https://www.npmjs.com/package/nodegod">nodegod</a><br />
        2012
      </dt>
      <dd>
        An app that manages remote Node.js instances<br />
        <a href="https://github.com/haraldrudell/nodegod">source code</a>
      </dd>
      <dt>
        <a href="https://www.npmjs.com/package/react-rollup">react-rollup</a><br />
        November 2018
      </dt>
      <dd>
        A Rollup preset for transpiling React applications server-side<br />
        <a href="https://github.com/haraldrudell/react-rollup">source code</a>
      </dd>
      <dt>
        <a href="https://www.npmjs.com/package/rollup-preset-esnext">rollup-preset-esnext</a><br />
        November 2018
      </dt>
      <dd>
        A self-contained ES.Next configurable Rollup preset
      </dd>
      <dt>
        <a href="https://www.npmjs.com/package/ssr-create-react-app">ssr-create-react-app</a><br />
        December 2018
      </dt>
      <dd><Row>
        <div>
          Adds server-side rendering to a Create React App<br />
          demonstration project: <a href="https://github.com/haraldrudell/ssr">demo-ssr</a><br />
          tweeted to <strong>Dan Abramov</strong> after he said “<a href="https://twitter.com/dan_abramov/status/953296173116276736">SSR is hard</a>”<br />
          video: <a href="https://youtu.be/sn9InqfNtSQ">Server-side rendering for create react app, 11 min</a><br />
          <a href="https://github.com/haraldrudell/ssr-create-react-app">source code</a>
        </div>
        <a href="https://www.npmjs.com/package/ssr-create-react-app">
          <Img src={cover} alt="faksimile" />
        </a>
      </Row></dd>
      <dt>
        <a href="https://www.npmjs.com/package/tagfinder">tagfinder</a><br />
        2012
      </dt>
      <dd>
        An html5 parser using ECMAScript RegExp<br />
        <a href="https://github.com/haraldrudell/tagfinder">source code</a>
      </dd>
    </dl>

    <h2>Other Open-Source Projects</h2>
    <dl>
      <dt><a href="https://github.com/haraldrudell/">github Profile</a></dt>
      <dd>haraldrudell 64+ repositories since 2011</dd>
      <dt>
        <a href="https://github.com/haraldrudell/ethereum">ethereum</a><br />
        November 2018
      </dt>
      <dd>
        An api gateway for Ethereum written in Golang<br />
        features phased deployment to Docker and Go 1.11 modules
      </dd>
      <dt>
        <a href="https://github.com/haraldrudell/parsify">parsify</a><br />
        November 2018
      </dt>
      <dd>A declarative JSON parser written in Golang</dd>
      <dt>
        <a href="https://github.com/haraldrudell/CSipSimple-usb">CSipSimple-usb</a><br />
        2016
      </dt>
      <dd>An Android Java app providing SIP calling via usb cable</dd>
    </dl>

    <p>
      This page was created using unreleased <strong>react@next</strong>,&nbsp;
      <strong>styled-components</strong> and&nbsp;
      <strong>pure css3</strong> features like grid
    </p>
    <h2>© 2018-present Harald Rudell&emsp;<a href="mailto:harald.rudell@gmail.com">harald.rudell@gmail.com</a>&emsp;<a href="http://www.haraldrudell.com">http://www.haraldrudell.com</a></h2>
</Margin>})

const FlexRow = styled.div`
display: flex
> :first-child {
  margin-right: 1em;
}
`
const Row = memo(({children}) => <FlexRow>{children}</FlexRow>)

const FiftyPt = styled.span`
font-size: 50pt;
`
// eslint-disable-next-line jsx-a11y/accessible-emoji
const LoudSpeaker = memo(() => <FiftyPt aria-label="loudspeaker" role="img">📢</FiftyPt>)
